import { inject, Injectable } from "@angular/core";
import { NGXLogger } from "ngx-logger";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import fileDownload from "js-file-download";

import { UserService } from "./user.service";
import { BoxService } from "./box.service";
import { ContainerService } from "./container.service";
import {
  ENVIRONMENT,
  Pageable,
  Project,
  User,
  Container,
  ContainerFile,
  UserImage,
  QuoteItem,
  BoxType,
  BoardType,
  BoardStrength,
} from "@company-box/models";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";

export interface BoxTemplateInput {
  subcategoryCode: string;
  boxCode: string;
  length: number;
  width: number;
  depth: number;
  kraftType: string;
  boardStrength?: BoardStrength;
  boardType?: BoardType;
  preset?: boolean;
  boxName?: string;
  selectedVariant?: { name?: string };
  shape?: string;
  isTape?: boolean;
  isTissue?: boolean;
  isLabel?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class ProjectService {
  userService: UserService = inject(UserService);
  protected httpClient: HttpClient = inject(HttpClient);
  protected logger: NGXLogger = inject(NGXLogger);
  protected environment: any = inject(ENVIRONMENT);
  private sanitizer: DomSanitizer = inject(DomSanitizer);
  private boxService: BoxService = inject(BoxService);
  private containerService: ContainerService = inject(ContainerService);

  constructor() {}

  getBasicDesigns() {
    return this.getDesignsOfType("basic");
  }

  /** Offline designs: projects created from an uploaded PDF (type ADVANCED). */
  getAdvancedDesigns() {
    return this.getDesignsOfType("advanced");
  }

  private getDesignsOfType(designType: "basic" | "advanced") {
    return new Promise((resolve, reject) => {
      this.userService.getCurrentUser().then(
        (user: { id: any }) => {
          this.httpClient
            .get(`${this.environment.apiHost}/project/design/${user.id}/list?designType=${designType}&theme=`)
            .subscribe(
              (response: any) => {
                resolve(response);
              },
              (error) => {
                reject(error || error.message);
              }
            );
        },
        (error: { message: any }) => {
          this.logger.debug(error.message || error);
          reject(false);
        }
      );
    });
  }
  createOrderItem(design: any) {
    const headers = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };

    return new Promise<any>((resolve, reject) => {
      const payload = JSON.stringify(design);
      this.httpClient.post(`${this.environment.apiHost}/project/design/order/?clone=false`, payload, headers).subscribe(
        (response) => {
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  getDownloadLink(container: string, fileset: string, downloadAs?: string): string {
    const base = `${this.environment.apiHost}/project/download/${container}/${fileset}`;
    return downloadAs ? `${base}?downloadAs=${encodeURIComponent(downloadAs)}` : base;
  }

  listProjects(
    userId: number,
    page: number,
    options: ProjectSearchOptions = {
      includeUser: false,
      includeType: false,
      createdBy: "ALL",
    }
  ): Promise<Pageable<Project>> {
    // this.logger.debug('Loading projects');
    let url = `${this.environment.apiHost}/project/design/`;
    if (userId !== 0) {
      url += userId;
    } else {
      url += "_";
    }
    url += "/list";
    if (userId !== 0) {
      url += `/page`;
    }
    url += `?includeScreenshot=false&includeContent=false&`;

    if (page > 1) {
      const offset = page - 1;
      url += `offset=${offset}&`;
    }
    if (options.pageSize) {
      url += `limit=${options.pageSize}&`;
    }
    if (options.includeUser) {
      url += `includeUser=${options.includeUser}&`;
    }
    if (options.includeType) {
      url += `includeType=${options.includeType}&`;
    }
    if (options.createdBy) {
      url += `createdBy=${options.createdBy}&`;
    }
    if (options.subcategory) {
      url += `subcategory=${options.subcategory}&`;
    }
    if (options.designType) {
      url += `designType=${options.designType}&`;
    }
    if (options.preset) {
      url += `preset=${options.preset}&`;
    }

    url += `sort=-createdTime`;
    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (response: any) => {
          const list: Project[] = [];
          (response.content as any[]).forEach((object) => {
            const project: Project = new Project().deserialize(object);
            if (!project.user) {
              if (project.userId && project.userId !== 0) {
                this.userService.getUser(project.userId).then((res) => {
                  project.user = new User().deserialize(res);
                });
              } else {
                project.user = User.EMPTY;
              }
            }
            list.push(project);
          });
          const newPage: Pageable<Project> = new Pageable<Project>().deserialize(response);
          newPage.content = list;
          this.logger.debug("Loaded projects", newPage);
          resolve(newPage);
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  getProjectById(
    projectId: number,
    includeUser: boolean,
    includeType: boolean,
    includeScreenshot: boolean
  ): Promise<Project> {
    // this.logger.debug('Loading project ' + projectId + ' by user ' + userId);
    let url = `${this.environment.apiHost}/project/design/_/${projectId}?includeUser=${includeUser}&includeType=${includeType}`;
    if (includeScreenshot) {
      url += `&includeScreenshot=${includeScreenshot}`;
    }
    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (response: any) => {
          const project: Project = new Project().deserialize(response);
          this.logger.debug("Loaded project", project);
          resolve(project);
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  getProjectByContainer(userId: string, containerName: string, includeUser: boolean, includeType: boolean, includeScreenshot = false): Promise<Project> {
    return new Promise((resolve, reject) => {
        const url = `${this.environment.apiHost}/project/design/${userId}/container/${containerName}?includeUser=${includeUser}&includeType=${includeType}&includeScreenshot=${includeScreenshot}`;
        return this.httpClient.get(url).subscribe(
          (response: any) => {
            const project: Project = new Project().deserialize(response);
            this.logger.debug("Loaded project", project);
            resolve(project);
          },
          (error) => {
            this.logger.debug(error.message || error);
            reject(error.message || error);
          }
        )
    })
  }

  getContainerFiles(container: string): Promise<Container> {
    return new Promise((resolve, reject) => {
      // this.logger.debug('Loading container ' + container);
      const url = `${this.environment.apiHost}/project/container/${container}/files?includeContent=false`;
      this.httpClient.get(url).subscribe(
        (response: any) => {
          resolve(new Container().deserialize(response));
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  getContainerFileDescriptor(container: string, filename: string): Promise<ContainerFile> {
    return new Promise((resolve, reject) => {
      // this.logger.debug('Loading container ' + container);
      const url = `${this.environment.apiHost}/project/container/${container}/files/content?filename=${filename}`;
      this.httpClient.get(url).subscribe(
        (response: any) => {
          resolve(new ContainerFile().deserialize(response.file));
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  getContainerFileUrl(container: string, filename: string) {
    return `${this.environment.apiHost}/project/container/${container}/${filename}`;
  }

  getFileUrl(partial: string) {
    return `${this.environment.apiHost}/project/${partial}`;
  }

  getImages(userId: number): Promise<UserImage[]> {
    const url = `${this.environment.apiHost}/project/image/${userId}/list`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res: any) => {
          const images: UserImage[] = [];
          res.forEach((value: any) => {
            const userImage: UserImage = new UserImage().deserialize(value);
            this.logger.debug("Loaded user images", userImage);
            images.push(userImage);
          });
          resolve(images);
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  getImage(userId: number, name: string): Promise<UserImage> {
    const url = `${this.environment.apiHost}/project/image/${userId}/${name}`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res: any) => {
          const userImage: UserImage = new UserImage().deserialize(res);
          this.logger.debug("Loaded user images", userImage);
          resolve(userImage);
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  getImageWithContent(userId: number, name: string): Promise<UserImage> {
    const url = `${this.environment.apiHost}/project/image/${userId}/${name}/dataUrl`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res: any) => {
          const userImage: UserImage = new UserImage().deserialize(res);
          this.logger.debug("Loaded user images", userImage);
          resolve(userImage);
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  getDesignUploadUrl(container: string, filename: string, convert: boolean = true): string {
    return `${this.environment.apiHost}/zuul/project/container/${container}/upload/design?filename=${filename}&convert=${convert}`;
  }

  getProofUploadUrl(container: string): string {
    return `${this.environment.apiHost}/zuul/project/container/${container}/proof`;
  }

  getTemplateForQuoteItem(quoteItem: QuoteItem): Promise<any> {
    const url =
      `${this.environment.apiHost}/project/container/?code=${quoteItem.type?.code}&boxType=${quoteItem.type?.aeCode}&` +
      `flute=${quoteItem.boardType?.code}&boxLength=${quoteItem.length}&boxWidth=${quoteItem.width}&boxDepth=${quoteItem.depth}&`;

    return new Promise((resolve, reject) => {
      this.httpClient.post(url, {}).subscribe(
        (response: any) => {
          const result = {
            template: response.template,
            container: response.container,
          };
          this.logger.debug("Created template for quote item", quoteItem, result);
          resolve(result);
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  getTemplate(input: BoxTemplateInput, downloadOnly = false): Promise<any> {
    const dimensions = `${input.length} x ${input.width} x ${input.depth}`;

    const template: any = {
      boardType: input.boxCode,
      boxCode: input.boxCode,
      kraftType: input.kraftType,
      boardStrength: input.boardStrength,
      boardStrengthName: input.boardStrength
        ? (input.boardStrength.strength ?? "") + (input.boardType?.flute ?? "")
        : null,
      flute: input.isTape || input.isTissue || input.isLabel ? "zflute" : input.boardType?.flute ?? "",
      boxLength: input.length,
      boxWidth: input.width,
      boxDepth: input.depth,
      dimensions,
      preset: input.preset ?? false,
      boxName: input.boxName,
      boxType: undefined,
      boardTypeName: undefined,
      templateName: undefined,
      containerName: undefined,
    };

    return new Promise((resolve, reject) => {
      this.boxService
        .getType(input.boxCode)
        .then((boxType: BoxType) => {
          template.boxType = boxType;

          if (input.boxCode?.startsWith("SNAP2") && input.selectedVariant) {
            const code = boxType.code ?? "";
            boxType.aeCode = code;
            const variantName = input.selectedVariant.name;
            if (variantName === "Super Seal" && input.length === 6) {
              boxType.aeCode = code.slice(0, 5) + "S662" + code.slice(5, 6);
            } else if (variantName === "Super Seal" && input.length === 9) {
              boxType.aeCode = code.slice(0, 5) + "S963" + code.slice(5, 6);
            } else if (variantName === "Super Seal" && input.length === 12) {
              boxType.aeCode = code.slice(0, 5) + "S1294" + code.slice(5, 6);
            } else if (variantName === "Returnable Super Seal" && input.length === 9 && input.depth === 3.5) {
              boxType.aeCode = code.slice(0, 5) + "S9635" + code.slice(5, 6);
            } else if (variantName === "Returnable Super Seal" && input.length === 6 && input.depth === 3.5) {
              boxType.aeCode = code.slice(0, 5) + "S6635" + code.slice(5, 6);
            } else if (variantName === "Returnable Super Seal" && input.length === 12) {
              boxType.aeCode = code.slice(0, 5) + "S1294" + code.slice(5, 6);
            }
          }

          const dimensionsArr = template.dimensions.replace(/&quot;/g, "").split("x");
          const lengthName = dimensionsArr[0].replace(/ /g, "").replace(".", "_") + "_";
          const widthName = dimensionsArr[1].replace(/ /g, "").replace(".", "_") + "_";
          const depthName = dimensionsArr[2].replace(/ /g, "").replace(".", "_");
          template.boardTypeName =
            input.isTape || input.isTissue || input.isLabel
              ? null
              : boxType?.boardTypeCode + (template.boardStrength ? "-" + template.boardStrength.code : "");
          template.flute = input.isTape || input.isTissue || input.isLabel ? "zflute" : boxType?.boardType?.code;
          template.templateName =
            boxType?.aeCode + "-" + template.boardTypeName + "-" + lengthName.trim() + widthName.trim() + depthName.trim();

          const fetchTemplate = () => {
            if (input.isTape) {
              return this.containerService.getTapeTemplate(template);
            }
            if (input.isTissue) {
              return this.containerService.getTissueTemplate(template);
            }
            if (input.isLabel) {
              const shape = (input.shape ?? "").toLowerCase();
              let labelType: string;
              if (shape.includes("circle")) {
                labelType = "LBLCIR";
              } else if (shape.includes("oval")) {
                labelType = "LBLOVL" + input.length + input.width;
              } else {
                labelType = "LBLREC";
              }
              return this.containerService.getLabelTemplate(template, labelType);
            }
            return this.containerService.getTemplate(template);
          };

          fetchTemplate()
            .then((response: any) => {
              template.containerName = response.container;
              template.templateName = response.template;
              resolve(downloadOnly ? response : template);
            })
            .catch((error: any) => reject(error));
        })
        .catch((reason: any) => reject(reason));
    });
  }

  createContainer(quoteItem: QuoteItem): Promise<string> {
    return new Promise((resolve, reject) => {
      this.userService.getCurrentUser().then(
        (user) => {
          this.getTemplateForQuoteItem(quoteItem).then(
            (templateAndContainer) => {
              const result = templateAndContainer.container;
              this.logger.debug("Created container for quote item", quoteItem, result);
              resolve(result);
            },
            (reason) => {
              this.logger.debug(reason);
              reject(reason);
            }
          );
        },
        (reason) => {
          this.logger.debug(reason);
          reject(reason);
        }
      );
    });
  }

  updateProofing(project: Project): Promise<Project> {
    const url = `${this.environment.apiHost}/project/design/${project.id}/proofing`;
    return new Promise<Project>((resolve, reject) => {
      this.httpClient
        .put(url, {
          workableId: project.workableId,
          created: project.proofCreated,
          approved: project.proofApproved,
          url: project.proofUrl,
        })
        .subscribe(
          (response: any) => {
            const updatedProject: Project = new Project().deserialize(response);
            this.logger.debug("Updated proofing for project", project);
            resolve(updatedProject);
          },
          (error) => {
            this.logger.debug(error);
            reject(error);
          }
        );
    });
  }

  getTemplateDownloadUrl(code: string, aeCode: string, flute: string | undefined,
                         length: number, width: number, depth: number): SafeUrl {

    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `${this.environment.apiHost}/project/container/?code=${code}&boxType=${aeCode}&` +
      `flute=${flute}&boxLength=${length}&boxWidth=${width}&boxDepth=${depth}&`
    );
  }
}

export interface ProjectSearchOptions {
  includeUser?: boolean;
  includeType?: boolean;
  createdBy?: string;
  pageSize?: number | 10;
  subcategory?: string;
  designType?: string;
  preset?: string;
}
